import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { formatApiError } from "../lib/api";
import { supabase } from "../lib/supabase";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { HeartPulse } from "lucide-react";

export default function Register() {
  const { t } = useLang();
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [weight, setWeight] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const numericWeight = Number(weight);

    if (!weight.trim()) {
      toast.error("Weight is required.");
      return;
    }

    if (!Number.isFinite(numericWeight) || numericWeight <= 0) {
      toast.error("Weight must be a number greater than 0.");
      return;
    }

    setLoading(true);
    const res = await register(name, email, password);

    if (res.ok) {
      const userId = res.data?.user?.id;

      if (userId) {
        const { error } = await supabase.from("profiles").upsert({
          id: userId,
          full_name: name,
          weight_kg: numericWeight,
        }, { onConflict: "id" });

        if (error) {
          setLoading(false);
          toast.error(formatApiError(error));
          return;
        }
      }
    }

    setLoading(false);
    if (res.ok) { toast.success(t("welcome_to_app")); nav("/dashboard"); }
    else toast.error(res.error);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 justify-center mb-8">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: "var(--rs-primary)" }}>
            <HeartPulse className="w-6 h-6 text-white" />
          </div>
          <span className="font-extrabold text-2xl" style={{ fontFamily: "Manrope" }}>{t("brand")}</span>
        </Link>
        <div className="bento-card">
          <h1 className="text-2xl font-extrabold mb-1">{t("register")}</h1>
          <p className="text-sm text-muted-foreground mb-6">{t("register_subtitle")}</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="name">{t("name")}</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1 h-12 text-base" data-testid="register-name" />
            </div>
            <div>
              <Label htmlFor="weight">Weight (kg)</Label>
              <Input id="weight" type="number" min="0.1" step="0.1" value={weight} onChange={(e) => setWeight(e.target.value)} required className="mt-1 h-12 text-base" data-testid="register-weight" />
            </div>
            <div>
              <Label htmlFor="email">{t("email")}</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 h-12 text-base" data-testid="register-email" />
            </div>
            <div>
              <Label htmlFor="password">{t("password")}</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="mt-1 h-12 text-base" data-testid="register-password" />
            </div>
            <Button type="submit" disabled={loading} className="btn-primary w-full h-12 text-base" data-testid="register-submit">
              {loading ? "…" : t("register")}
            </Button>
          </form>
          <div className="mt-6 text-center text-sm text-muted-foreground">
            {t("have_account")} <Link to="/login" className="font-semibold text-primary hover:underline" data-testid="goto-login">{t("login")}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
